import { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, MapPin, Maximize2, DoorOpen, BedDouble, Bath, Car, ArrowUpDown,
  Leaf, Wallet, Play, CalendarCheck, MessageCircle, Check, Heart, Building2, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { formatPrezzo, formatEuro } from "@/data/properties";
import { api, fileUrl } from "@/lib/api";
import { waLink } from "@/lib/brand";
import { useApp } from "@/context/AppState";
import FloorPlan from "@/components/FloorPlan";
import PropertyCard from "@/components/PropertyCard";
import useSEO from "@/hooks/useSEO";

const ENERGY_COLORS = {
  A: "#0F5C4A", B: "#3f8f5f", C: "#8bab3d", D: "#d9b53a", E: "#e08b2f", F: "#d9663a", G: "#c0392b",
};

const IMG_FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

function MortgageSim({ prezzo }) {
  const [importo, setImporto] = useState(Math.round(prezzo * 0.8));
  const [tasso, setTasso] = useState(3.5);
  const [anni, setAnni] = useState(25);
  const rata = useMemo(() => {
    const r = tasso / 100 / 12;
    const n = anni * 12;
    if (r === 0) return importo / n;
    return (importo * r) / (1 - Math.pow(1 + r, -n));
  }, [importo, tasso, anni]);

  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-6">
      <h3 className="font-display text-2xl font-semibold text-anthracite">Simulazione della rata</h3>
      <p className="text-sm text-anthracite/50 mt-1">Stima puramente indicativa, non costituisce offerta.</p>
      <div className="mt-5 space-y-5">
        <div>
          <div className="flex justify-between text-sm mb-2"><span className="text-anthracite/60">Importo mutuo</span><span className="font-semibold text-brand">{formatEuro(importo)}</span></div>
          <Slider value={[importo]} min={20000} max={prezzo} step={5000} onValueChange={([v]) => setImporto(v)} data-testid="sim-importo" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex justify-between text-sm mb-2"><span className="text-anthracite/60">Tasso</span><span className="font-semibold text-brand">{tasso.toFixed(1)}%</span></div>
            <Slider value={[tasso]} min={1} max={6} step={0.1} onValueChange={([v]) => setTasso(v)} data-testid="sim-tasso" />
          </div>
          <div>
            <div className="flex justify-between text-sm mb-2"><span className="text-anthracite/60">Durata</span><span className="font-semibold text-brand">{anni} anni</span></div>
            <Slider value={[anni]} min={5} max={30} step={1} onValueChange={([v]) => setAnni(v)} data-testid="sim-anni" />
          </div>
        </div>
        <div className="rounded-xl bg-brand text-white p-5 flex items-center justify-between">
          <span className="text-sm text-white/80">Rata mensile stimata</span>
          <span className="font-display text-3xl font-semibold" data-testid="sim-rata">{formatEuro(rata)}</span>
        </div>
      </div>
    </div>
  );
}

function VisitModal({ open, onOpenChange, property }) {
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", data: "" });
  const [consenso, setConsenso] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono) { toast.error("Inserisci nome e telefono."); return; }
    if (!consenso) { toast.error("Accetta l'informativa privacy."); return; }
    try {
      await api.post("/public/leads", {
        tipo: "visita",
        contatto: { nome: form.nome, telefono: form.telefono, email: form.email || null },
        payload: { Immobile: property.titolo, Zona: `${property.comune} — ${property.zona}`, "Data preferita": form.data || "—" },
        consensoPrivacy: true,
        provenienza: `Dettaglio immobile: ${property.titolo}`,
        immobileId: property.id,
      });
      toast.success("Richiesta inviata! Ti contatteremo a breve.");
      onOpenChange(false);
      setForm({ nome: "", telefono: "", email: "", data: "" }); setConsenso(false);
    } catch { toast.error("Invio non riuscito, riprova."); }
  };
  const fld = "bg-white border-border-subtle focus-visible:ring-brand/30 focus-visible:border-brand rounded-xl h-12";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-warm border-0 rounded-3xl" data-testid="visit-modal">
        <DialogHeader><DialogTitle className="font-display text-3xl font-semibold text-anthracite">Prenota una visita</DialogTitle></DialogHeader>
        <p className="text-sm text-anthracite/60 -mt-2">{property.titolo}</p>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="visit-form">
          <Input className={fld} placeholder="Nome e cognome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="visit-nome" />
          <Input className={fld} placeholder="Telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="visit-telefono" />
          <Input className={fld} type="email" placeholder="Email (per la conferma)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="visit-email" />
          <Input className={fld} type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="visit-data" />
          <label className="flex items-start gap-2 text-xs text-anthracite/60">
            <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)} className="mt-0.5 accent-[#0F5C4A]" data-testid="visit-privacy" />
            Acconsento al trattamento dei dati secondo l'informativa privacy.
          </label>
          <button type="submit" className="w-full rounded-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors" data-testid="visit-submit">
            Invia richiesta
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ImmobileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [allProps, setAllProps] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const { favorites, toggleFavorite } = useApp();

  useEffect(() => {
    setNotFound(false); setActiveImg(0);
    api.get(`/public/properties/${id}`).then((r) => setProperty(r.data)).catch(() => setNotFound(true));
    api.get("/public/properties").then((r) => setAllProps(r.data)).catch(() => {});
  }, [id]);

  const similar = useMemo(
    () => property ? allProps.filter((p) => p.id !== property.id && (p.comune === property.comune || p.tipo === property.tipo)).slice(0, 3) : [],
    [property, allProps]
  );

  useSEO({
    title: property ? `${property.titolo} — ${property.comune} | Casamore Group` : "Immobile | Casamore Group",
    description: property ? (property.descrizione || `${property.titolo} a ${property.comune}. Scopri i dettagli con Casamore Group.`).slice(0, 155) : "Scheda immobile Casamore Group",
    canonical: `${window.location.origin}/immobili/${id}`,
  });

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="text-anthracite/60">Immobile non trovato.</p>
        <Link to="/immobili" className="text-brand font-semibold">Torna agli immobili</Link>
      </div>
    );
  }
  if (!property) {
    return <div className="pt-40 pb-32 text-center text-anthracite/40">Caricamento…</div>;
  }

  const images = (property.immagini || []).map(fileUrl);
  const isFav = favorites.includes(property.id);
  const specs = [
    { icon: Maximize2, label: `${property.superficie} mq` },
    { icon: DoorOpen, label: `${property.locali} locali` },
    { icon: BedDouble, label: `${property.camere} camere` },
    { icon: Bath, label: `${property.bagni} bagni` },
    { icon: Car, label: property.box ? "Box auto" : property.postoAuto ? "Posto auto" : "No box" },
    { icon: ArrowUpDown, label: property.ascensore ? "Ascensore" : "Senza ascensore" },
  ];

  return (
    <div className="pt-24 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-brand transition-colors mb-6" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Torna indietro
        </button>

        {/* Gallery */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-3 relative rounded-3xl overflow-hidden aspect-[16/10]">
            <img src={images[activeImg] || IMG_FALLBACK} alt={property.titolo} className="w-full h-full object-cover" data-testid="gallery-main" />
            {property.etichetta && (
              <span className="absolute top-4 left-4 rounded-full bg-brand text-white text-xs font-semibold px-3 py-1.5">{property.etichetta}</span>
            )}
          </div>
          <div className="lg:col-span-1 grid grid-cols-4 lg:grid-cols-1 gap-3">
            {images.slice(0, 3).map((img, i) => (
              <button key={`${img}-${i}`} onClick={() => setActiveImg(i)} className={`relative rounded-2xl overflow-hidden aspect-square lg:aspect-[16/10] ${activeImg === i ? "ring-2 ring-brand" : ""}`} data-testid={`thumb-${i}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
            <button onClick={() => setVideoOpen(true)} className="relative rounded-2xl overflow-hidden aspect-square lg:aspect-[16/10] group" data-testid="video-tile">
              <img src={images[3] || images[0]} alt="" className="w-full h-full object-cover brightness-75" />
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid place-items-center h-12 w-12 rounded-full bg-white/90 text-brand group-hover:scale-110 transition-transform"><Play className="h-5 w-5 ml-0.5" /></span>
              </span>
              <span className="absolute bottom-2 left-0 right-0 text-center text-white text-xs font-semibold">Video</span>
            </button>
          </div>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* LEFT column */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="font-display text-4xl sm:text-5xl font-semibold text-anthracite tracking-tight">{property.titolo}</h1>
                  <p className="mt-2 flex items-center gap-1.5 text-anthracite/60"><MapPin className="h-4 w-4 text-brand" /> {property.comune} — {property.zona}</p>
                </div>
                <button onClick={() => toggleFavorite(property.id)} className="grid place-items-center h-11 w-11 rounded-full border border-border-subtle hover:border-brand transition-colors shrink-0" data-testid="detail-fav">
                  <Heart className={`h-5 w-5 ${isFav ? "fill-brand text-brand" : "text-anthracite"}`} />
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {specs.map((s) => (
                  <span key={s.label} className="inline-flex items-center gap-2 rounded-full bg-white border border-border-subtle px-4 py-2 text-sm text-anthracite/80">
                    <s.icon className="h-4 w-4 text-brand" /> {s.label}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-3">Descrizione</h2>
              <p className="text-anthracite/70 leading-relaxed">{property.descrizione}</p>
            </div>

            {/* Punti di forza */}
            {property.puntiForza?.length > 0 && (
            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-4">Punti di forza</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {property.puntiForza.map((p) => (
                  <div key={p} className="flex items-center gap-3 rounded-xl bg-accent-subtle px-4 py-3">
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-brand text-white shrink-0"><Check className="h-4 w-4" /></span>
                    <span className="text-sm font-medium text-anthracite">{p}</span>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Caratteristiche */}
            {property.caratteristiche?.length > 0 && (
            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-4">Caratteristiche</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {property.caratteristiche.map((c) => (
                  <div key={c.label} className="rounded-xl border border-border-subtle bg-white p-4">
                    <p className="text-xs text-anthracite/50">{c.label}</p>
                    <p className="font-semibold text-anthracite mt-1">{c.value}</p>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Planimetria */}
            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-4">Planimetria</h2>
              <div className="rounded-2xl border border-border-subtle bg-white p-4">
                <FloorPlan className="w-full h-auto" />
              </div>
            </div>

            {/* Posizione */}
            <div>
              <h2 className="font-display text-3xl font-semibold text-anthracite mb-4">Posizione indicativa</h2>
              <div className="relative rounded-2xl overflow-hidden border border-border-subtle h-72 bg-[#EDF2EE]">
                <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(#d9e4dd 1px, transparent 1px), linear-gradient(90deg, #d9e4dd 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
                {property.coords && (
                <span className="absolute -translate-x-1/2 -translate-y-full" style={{ left: `${property.coords.x}%`, top: `${property.coords.y}%` }}>
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-white shadow-lg"><MapPin className="h-5 w-5" /></span>
                </span>
                )}
                <span className="absolute bottom-3 left-3 text-[11px] text-anthracite/50 bg-white/70 rounded-full px-3 py-1">Zona {property.zona}, {property.comune} • posizione dimostrativa</span>
              </div>
              {property.nascondiIndirizzo && (
                <p className="mt-2 text-xs text-anthracite/45">Indirizzo esatto riservato — verrà comunicato in fase di appuntamento.</p>
              )}
            </div>

            <MortgageSim prezzo={property.prezzo} />
          </div>

          {/* RIGHT sticky */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="rounded-3xl border border-border-subtle bg-white p-6 shadow-[0_20px_50px_-30px_rgba(15,92,74,0.4)]">
                <p className="text-sm text-anthracite/50 capitalize">{property.transazione} • {property.tipo}</p>
                <p className="font-display text-4xl font-semibold text-brand mt-1">{formatPrezzo(property)}</p>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-accent-subtle p-3 flex items-center gap-2">
                    <Leaf className="h-5 w-5" style={{ color: ENERGY_COLORS[property.classeEnergetica] }} />
                    <div>
                      <p className="text-[11px] text-anthracite/50">Classe</p>
                      <p className="font-bold" style={{ color: ENERGY_COLORS[property.classeEnergetica] }}>{property.classeEnergetica}</p>
                    </div>
                  </div>
                  <div className="rounded-xl bg-accent-subtle p-3 flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-brand" />
                    <div>
                      <p className="text-[11px] text-anthracite/50">Spese cond.</p>
                      <p className="font-bold text-anthracite">{property.speseCondominiali ? `€${property.speseCondominiali}` : "—"}</p>
                    </div>
                  </div>
                </div>

                <button onClick={() => setVisitOpen(true)} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors" data-testid="book-visit-btn">
                  <CalendarCheck className="h-4 w-4" /> Prenota una visita
                </button>
                <a href={waLink(`Salve, sono interessato all'immobile: ${property.titolo} (${property.comune}). Vorrei maggiori informazioni.`)} target="_blank" rel="noopener noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-brand text-brand font-semibold py-4 hover:bg-brand hover:text-white transition-colors" data-testid="detail-whatsapp-btn">
                  <MessageCircle className="h-4 w-4" /> Scrivi su WhatsApp
                </a>
              </div>

              {property.compatibilita != null && (
              <div className="rounded-3xl border border-border-subtle bg-brand text-white p-6">
                <p className="font-display text-2xl leading-tight">Compatibilità con i nostri acquirenti</p>
                <div className="mt-4 flex items-end gap-2">
                  <span className="font-display text-5xl font-semibold">{property.compatibilita}</span>
                  <span className="text-white/70 mb-1">/100</span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-white/20 overflow-hidden">
                  <div className="h-full bg-white rounded-full" style={{ width: `${property.compatibilita}%` }} />
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <div className="mt-20">
            <div className="flex items-center gap-2 mb-6">
              <Building2 className="h-5 w-5 text-brand" />
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-anthracite">Immobili simili</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {similar.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          </div>
        )}
      </div>

      {/* Video placeholder dialog */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-2xl bg-anthracite border-0 rounded-3xl p-0 overflow-hidden" data-testid="video-modal">
          <div className="relative aspect-video">
            <img src={images[0] || IMG_FALLBACK} alt="" className="w-full h-full object-cover brightness-50" />
            <div className="absolute inset-0 grid place-items-center text-center text-white p-6">
              <div>
                <span className="grid place-items-center h-16 w-16 rounded-full bg-white/20 mx-auto"><Play className="h-7 w-7 ml-1" /></span>
                <p className="mt-4 font-display text-2xl">Video tour in arrivo</p>
                <p className="text-white/70 text-sm mt-1">Contenuto dimostrativo — il video verrà pubblicato a breve.</p>
              </div>
            </div>
            <button onClick={() => setVideoOpen(false)} className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-white/20 text-white"><X className="h-4 w-4" /></button>
          </div>
        </DialogContent>
      </Dialog>

      <VisitModal open={visitOpen} onOpenChange={setVisitOpen} property={property} />
    </div>
  );
}
